import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '@config/api';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Fab,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Pets,
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  ArrowBack,
  PhotoCamera,
  PhotoLibrary,
  DeleteForever
} from '@mui/icons-material';
import { AccountTree as PedigreeIcon, FamilyRestroom as ReproIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { animalsService, Animal, CreateAnimalRequest } from '@services/animalsService';
import clientDashboardService, { ClientAppointment } from '@services/clientDashboardService';
import { useAuth } from '@contexts/AuthContext';
import { Tabs, Tab, Box as TabBox, Divider } from '@mui/material';
import AnimalFormDialog from '@shared/AnimalFormDialog';
import LitterDialog from '@shared/LitterDialog';
import PedigreeModal from '@shared/PedigreeModal';
import { 
  Assessment as AssessmentIcon,
  Vaccines as VaccinesIcon,
  LocalHospital as TreatmentIcon,
  Description as DocumentsIcon
} from '@mui/icons-material';
import ClientAnimalsModule from './ClientAnimalsModule';
import littersService from '@services/littersService';
import type { Litter } from '@app-types/index';

// Folosim interfata din animalsService

const ClinicAnimalsManagement: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAnimal, setSelectedAnimal] = useState<Animal | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Reproducere (lista fatari + dialog)
  const [openLitterDialog, setOpenLitterDialog] = useState(false);
  const [openPedigreeModal, setOpenPedigreeModal] = useState(false);
  const [litters, setLitters] = useState<Litter[]>([]);
  const [isLoadingLitters, setIsLoadingLitters] = useState(false);
  const [litterEditorOpen, setLitterEditorOpen] = useState(false);
  const [litterPupsOpen, setLitterPupsOpen] = useState(false);
  const [activeLitter, setActiveLitter] = useState<Litter | null>(null);
  const [litterEditNotes, setLitterEditNotes] = useState('');
  const [litterEditDate, setLitterEditDate] = useState('');
  const [litterSaving, setLitterSaving] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState<Animal | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Name editing states
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  
  // Sterilization states
  const [sterilizationStatus, setSterilizationStatus] = useState<'DA' | 'NU' | null>(null);
  const [sterilizationDate, setSterilizationDate] = useState('');
  const [savingSterilization, setSavingSterilization] = useState(false);
  
  // Irrevocable data states
  const [irrevocableEventDate, setIrrevocableEventDate] = useState('');
  const [irrevocableEventDetails, setIrrevocableEventDetails] = useState('');
  const [savingIrrevocable, setSavingIrrevocable] = useState(false);
  
  // Transfer states
  const [transferEmail, setTransferEmail] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [transferDetails, setTransferDetails] = useState('');
  const [savingTransfer, setSavingTransfer] = useState(false);
  const [emailVerificationStatus, setEmailVerificationStatus] = useState<'idle' | 'checking' | 'exists' | 'not_exists'>('idle');
  const [formData, setFormData] = useState<CreateAnimalRequest>({
    name: '',
    species: undefined,
    breed: '',
    gender: undefined,
    color: '',
    weight: undefined,
    microchipId: '',
    birthDate: undefined,
    profileImage: undefined
  });

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  
  // State pentru schimbare poză rapidă
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [medicalHistoryAppointments, setMedicalHistoryAppointments] = useState<ClientAppointment[]>([]);
  const [medicalDocuments, setMedicalDocuments] = useState<any[]>([]);
  const [loadingMedicalHistory, setLoadingMedicalHistory] = useState(false);
  const [medicalHistoryError, setMedicalHistoryError] = useState('');

  // Incarca animalele din DB - foloseste service-ul potrivit in functie de rol
  useEffect(() => {
    const loadAnimals = async () => {
      try {
        setIsLoading(true);
        let animalsData;
        
        if (currentUser?.role === 'client') {
          // Pentru clienti, folosim clientDashboardService
          const clientPatientsResponse = await clientDashboardService.getPatients();
          const clientPatients = Array.isArray(clientPatientsResponse) 
            ? clientPatientsResponse 
            : (clientPatientsResponse as any)?.data || [];
          
          animalsData = clientPatients.map((patient: any) => ({
            id: patient.id,
            name: patient.name,
            species: patient.species,
            breed: patient.breed,
            // Normalizeaza sexul la 'male' | 'female'
            gender: (() => {
              const g = (patient.gender ?? patient.sex ?? '').toString().toLowerCase();
              if (g === 'male' || g === 'm' || g === 'mascul') return 'male';
              if (g === 'female' || g === 'f' || g === 'femela' || g === 'femela') return 'female';
              return undefined as any;
            })(),
            color: patient.color,
            weight: patient.weight,
            microchipId: patient.microchip,
            birthDate: patient.birthdate,
            profileImage: patient.photo_url,
            allergies: Array.isArray(patient.allergies) ? patient.allergies : [],
            chronicConditions: Array.isArray(patient.chronicConditions) ? patient.chronicConditions : []
          }));
        } else {
          // Pentru admin, folosim animalsService
          animalsData = await animalsService.getAnimals();
        }
        
        setAnimals(animalsData);
      } catch (error) {
        console.error('Error loading animals:', error);
        setError('Eroare la incarcarea animalelor');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      loadAnimals();
    }
  }, [currentUser]);

  const reloadLitters = React.useCallback(async () => {
    if (!selectedAnimal) return;
    try {
      setIsLoadingLitters(true);
      const data = await littersService.getAnimalLitters(selectedAnimal.id, 'both');
      setLitters(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error loading litters', e);
      setLitters([]);
      setSnackbar({ open: true, message: 'Nu am putut încărca fătările.', severity: 'error' });
    } finally {
      setIsLoadingLitters(false);
    }
  }, [selectedAnimal]);

  useEffect(() => {
    if (!selectedAnimal || activeTab !== 5) return;
    reloadLitters();
  }, [selectedAnimal, activeTab, reloadLitters]);

  useEffect(() => {
    const loadOwnerSafeMedicalData = async () => {
      if (currentUser?.role !== 'client' || !selectedAnimal || ![0, 3].includes(activeTab)) {
        return;
      }

      try {
        setLoadingMedicalHistory(true);
        setMedicalHistoryError('');

        const [appointmentsResponse, documentsResponse] = await Promise.all([
          clientDashboardService.getAppointments({ limit: 200 }),
          clientDashboardService.getDocuments({ owner_type: 'patient', patient_id: selectedAnimal.id })
        ]);

        const animalAppointments = (appointmentsResponse.data || [])
          .filter((appointment) => appointment.patient?.id === selectedAnimal.id)
          .sort((a, b) => new Date(b.start_ts).getTime() - new Date(a.start_ts).getTime());

        setMedicalHistoryAppointments(animalAppointments);
        setMedicalDocuments(Array.isArray(documentsResponse) ? documentsResponse : []);
      } catch (loadError) {
        console.error('Error loading owner-safe medical data:', loadError);
        setMedicalHistoryError('Eroare la incarcarea istoricului medical al animalului.');
        setMedicalHistoryAppointments([]);
        setMedicalDocuments([]);
      } finally {
        setLoadingMedicalHistory(false);
      }
    };

    loadOwnerSafeMedicalData();
  }, [activeTab, currentUser?.role, selectedAnimal?.id]);

  // Set initial name when animal is selected
  useEffect(() => {
    if (selectedAnimal) {
      setNewName(selectedAnimal.name);
      // Set sterilization data
      setSterilizationStatus(selectedAnimal.isNeutered ? 'DA' : 'NU');
      setSterilizationDate(selectedAnimal.neuteredDate || '');
    }
  }, [selectedAnimal]);

  const handleMedicalDocumentDownload = async (documentId: string, filename: string) => {
    try {
      const { download_url } = await clientDashboardService.getDownloadUrl(documentId);
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (downloadError) {
      console.error('Error downloading medical document:', downloadError);
      setSnackbar({
        open: true,
        message: 'Eroare la descarcarea documentului medical',
        severity: 'error'
      });
    }
  };

  const handleAddAnimal = () => {
    setEditingAnimal(null);
    setFormData({
      name: '',
      species: undefined,
      breed: '',
      gender: undefined,
      color: '',
      weight: undefined,
      microchipId: '',
      birthDate: undefined,
      profileImage: undefined
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleEditAnimal = (animal: Animal) => {
    
    setEditingAnimal(animal);
    const formDataToSet = {
      name: animal.name || '',
      species: animal.species,
      breed: animal.breed || '',
      gender: animal.gender,
      color: animal.color || '',
      weight: animal.weight,
      microchipId: animal.microchipId || '', // Daca este null, se seteaza string gol
      birthDate: animal.birthDate,
      profileImage: animal.profileImage
    };
    
    setFormData(formDataToSet);
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleSaveName = async () => {
    if (!newName.trim() || !selectedAnimal) {
      setSnackbar({
        open: true,
        message: 'Numele nu poate fi gol.',
        severity: 'error'
      });
      return;
    }

    try {
      setSavingName(true);
      
      await animalsService.updateAnimal(selectedAnimal.id, {
        name: newName.trim()
      });
      
      // Update local state
      setSelectedAnimal({
        ...selectedAnimal,
        name: newName.trim()
      });
      
      // Update in animals list
      setAnimals(animals.map(animal => 
        animal.id === selectedAnimal.id 
          ? { ...animal, name: newName.trim() }
          : animal
      ));
      
      setEditingName(false);
      setSnackbar({
        open: true,
        message: 'Numele animalului a fost actualizat cu succes!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving name:', error);
      setSnackbar({
        open: true,
        message: 'Eroare la actualizarea numelui.',
        severity: 'error'
      });
    } finally {
      setSavingName(false);
    }
  };

  const openLitterEdit = async (litterId: string) => {
    try {
      const full = await littersService.getLitter(litterId);
      setActiveLitter(full);
      setLitterEditNotes(full.notes || '');
      setLitterEditDate(full.whelping_date ? String(full.whelping_date).slice(0, 10) : '');
      setLitterEditorOpen(true);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Nu am putut încărca fătarea.', severity: 'error' });
    }
  };

  const openLitterPups = async (litterId: string) => {
    try {
      const full = await littersService.getLitter(litterId);
      setActiveLitter(full);
      setLitterPupsOpen(true);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Nu am putut încărca detaliile fătării.', severity: 'error' });
    }
  };

  const saveLitterEdit = async () => {
    if (!activeLitter) return;
    try {
      setLitterSaving(true);
      await littersService.updateLitter(activeLitter.id, {
        ...(currentUser?.role === 'client' ? {} : { notes: litterEditNotes }),
        whelpingDate: litterEditDate,
      });
      setLitterEditorOpen(false);
      setSnackbar({ open: true, message: 'Fătare actualizată.', severity: 'success' });
      await reloadLitters();
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Salvare eșuată.', severity: 'error' });
    } finally {
      setLitterSaving(false);
    }
  };

  const deleteLitterRow = async (litterId: string) => {
    if (!window.confirm('Ștergi această fătare? Acțiunea este ireversibilă.')) return;
    try {
      await littersService.deleteLitter(litterId);
      setSnackbar({ open: true, message: 'Fătare ștearsă.', severity: 'success' });
      await reloadLitters();
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Ștergere eșuată.', severity: 'error' });
    }
  };

  const handleSaveSterilization = async () => {
    if (!selectedAnimal) return;

    try {
      setSavingSterilization(true);
      
      const updateData: any = {
        isNeutered: sterilizationStatus === 'DA'
      };
      
      if (sterilizationStatus === 'DA' && sterilizationDate) {
        updateData.neuteredDate = sterilizationDate;
      } else {
        updateData.neuteredDate = null;
      }
      
      await animalsService.updateAnimal(selectedAnimal.id, updateData);
      
      // Update local state
      setSelectedAnimal({
        ...selectedAnimal,
        isNeutered: updateData.isNeutered,
        neuteredDate: updateData.neuteredDate
      });
      
      // Update in animals list
      setAnimals(animals.map(animal => 
        animal.id === selectedAnimal.id 
          ? { ...animal, isNeutered: updateData.isNeutered, neuteredDate: updateData.neuteredDate }
          : animal
      ));
      
      setSnackbar({
        open: true,
        message: 'Datele de sterilizare au fost salvate cu succes!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving sterilization data:', error);
      setSnackbar({
        open: true,
        message: 'Eroare la salvarea datelor de sterilizare.',
        severity: 'error'
      });
    } finally {
      setSavingSterilization(false);
    }
  };

  const handleMarkAsDeceased = async () => {
    if (!selectedAnimal || !irrevocableEventDate || !irrevocableEventDetails) {
      setSnackbar({
        open: true,
        message: 'Te rog completeaza toate campurile obligatorii.',
        severity: 'error'
      });
      return;
    }

    const confirmMessage = `Confirmare marcarea pacientului "In amintire"\n\nEști sigur că vrei să marchezi acest pacient ca fiind în amintire? Această acțiune este permanentă și nu mai poate fi anulată ulterior. Dosarul medical va rămâne disponibil doar pentru consultare, fără posibilitatea de editare.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSavingIrrevocable(true);
      
      await animalsService.updateAnimal(selectedAnimal.id, {
        status: 'deceased',
        deceasedAt: irrevocableEventDate,
        deceasedReason: irrevocableEventDetails
      });
      
      setSnackbar({
        open: true,
        message: 'Pacientul a fost marcat ca fiind în amintire.',
        severity: 'success'
      });
      
      // Reload animals list
      window.location.reload();
      
    } catch (error) {
      console.error('Error marking animal as deceased:', error);
      setSnackbar({
        open: true,
        message: 'A apărut o eroare la procesare. Încearcă din nou.',
        severity: 'error'
      });
    } finally {
      setSavingIrrevocable(false);
    }
  };

  const handleEmailVerification = async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailVerificationStatus('idle');
      return;
    }

    try {
      setEmailVerificationStatus('checking');
      
      // Verifică dacă email-ul există în baza de date
      const response = await fetch(`${API_BASE_URL}/users/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setEmailVerificationStatus('exists');
      } else {
        setEmailVerificationStatus('not_exists');
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailVerificationStatus('idle');
    }
  };

  const handleTransferAnimal = async () => {
    if (!selectedAnimal || !transferEmail || !transferDate) {
      setSnackbar({
        open: true,
        message: 'Te rog completează toate câmpurile obligatorii.',
        severity: 'error'
      });
      return;
    }

    const confirmMessage = `Confirmare înstrăinare pacient\n\nEști sigur că vrei să înstrăinezi acest pacient către ${transferEmail}? Această acțiune va trimite o notificare către noul proprietar.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSavingTransfer(true);
      
      await animalsService.updateAnimal(selectedAnimal.id, {
        status: 'transferred',
        deceasedAt: transferDate, // Reusing field for transfer date
        deceasedReason: `Înstrăinat către: ${transferEmail}. ${transferDetails}`
      });
      
      // Trimite notificarea către noul proprietar
      await fetch(`${API_BASE_URL}/animals/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          animalId: selectedAnimal.id,
          newOwnerEmail: transferEmail,
          transferDate: transferDate,
          transferDetails: transferDetails,
          currentOwnerName: currentUser?.firstName + ' ' + currentUser?.lastName
        })
      });
      
      setSnackbar({
        open: true,
        message: 'Pacientul a fost înstrăinat cu succes. Noul proprietar va primi o notificare.',
        severity: 'success'
      });
      
      // Reload animals list
      window.location.reload();
      
    } catch (error) {
      console.error('Error transferring animal:', error);
      setSnackbar({
        open: true,
        message: 'A apărut o eroare la înstrăinare. Încearcă din nou.',
        severity: 'error'
      });
    } finally {
      setSavingTransfer(false);
    }
  };


  const handleDeleteAnimal = async (id: string) => {
    if (window.confirm('Esti sigur ca vrei sa stergi acest animal?')) {
      try {
        await animalsService.deleteAnimal(id);
        setAnimals(animals.filter(animal => animal.id !== id));
        setSnackbar({
          open: true,
          message: 'Animal sters cu succes!',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting animal:', error);
        setSnackbar({
          open: true,
          message: 'Eroare la stergerea animalului',
          severity: 'error'
        });
      }
    }
  };

  // Handler pentru schimbare poză rapidă
  const handlePhotoClick = (e: React.MouseEvent, animalId: string) => {
    e.stopPropagation();
    setUploadingPhotoFor(animalId);
    fileInputRef.current?.click();
  };

  // Handler pentru upload poză
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingPhotoFor) return;

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result as string;
        
        try {
          // Actualizează animalul cu noua poză
          const updatedAnimal = await animalsService.updateAnimal(uploadingPhotoFor, {
            profileImage: base64Image
          });
          
          // Actualizează lista de animale
          setAnimals(animals.map(animal => 
            animal.id === uploadingPhotoFor ? { ...animal, profileImage: base64Image } : animal
          ));
          
          // Actualizează și animalul selectat dacă este cazul
          if (selectedAnimal && selectedAnimal.id === uploadingPhotoFor) {
            setSelectedAnimal({ ...selectedAnimal, profileImage: base64Image });
          }
          
          setSnackbar({
            open: true,
            message: 'Poza a fost actualizată cu succes!',
            severity: 'success'
          });
        } catch (error) {
          console.error('Error updating photo:', error);
          setSnackbar({
            open: true,
            message: 'Eroare la actualizarea pozei',
            severity: 'error'
          });
        } finally {
          setUploadingPhotoFor(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      setSnackbar({
        open: true,
        message: 'Eroare la citirea fișierului',
        severity: 'error'
      });
      setUploadingPhotoFor(null);
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      errors.name = 'Nu ati introdus numele animalului';
    }

    if (!formData.breed.trim()) {
      errors.breed = 'Nu ati introdus rasa animalului';
    }

    if (!formData.color.trim()) {
      errors.color = 'Nu ati introdus culoarea animalului';
    }

    if (!formData.weight || formData.weight <= 0) {
      errors.weight = 'Nu ati introdus greutatea animalului';
    }

    if (!formData.microchipId?.trim()) {
      errors.microchipId = 'Nu ati introdus microchip-ul animalului';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAnimal = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingAnimal) {
        const updatedAnimal = await animalsService.updateAnimal(editingAnimal.id, formData);
        setAnimals(animals.map(animal => 
          animal.id === editingAnimal.id ? updatedAnimal : animal
        ));
        setSnackbar({
          open: true,
          message: 'Animal actualizat cu succes!',
          severity: 'success'
        });
      } else {
        const newAnimal = await animalsService.createAnimal(formData);
        setAnimals([...animals, newAnimal]);
        setSnackbar({
          open: true,
          message: 'Animal adaugat cu succes!',
          severity: 'success'
        });
      }
      setOpenDialog(false);
      setFormErrors({});
    } catch (error: any) {
      console.error('Error saving animal:', error);
      let errorMessage = 'Eroare la salvarea animalului';
      
      if (error.message && error.message.includes('microchip')) {
        errorMessage = 'Un animal cu acest microchip exista deja in baza de date';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const filteredAnimals = animals.filter(animal => {
    const matchesSearch = animal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         animal.breed.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || animal.species === filterType;
    return matchesSearch && matchesFilter;
  });
  const completedMedicalHistory = medicalHistoryAppointments.filter(appointment => appointment.status === 'completed');

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          🐾 Animalele mele
        </Typography>
      </Box>

      {/* Search and Filter */}
      <Box display="flex" gap={2} mb={3} flexDirection={{ xs: 'column', sm: 'row' }}>
        <TextField
          placeholder="Cauta dupa nume sau rasa..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Tip animal</InputLabel>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            label="Tip animal"
          >
            <MenuItem value="all">Toate</MenuItem>
             <MenuItem value="dog">Caini</MenuItem>
             <MenuItem value="cat">Pisici</MenuItem>
             <MenuItem value="bird">Pasari</MenuItem>
             <MenuItem value="rabbit">Rozatoare</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddAnimal}
          disabled={currentUser?.role === 'client'}
          sx={{ 
            backgroundColor: '#059669', // Verde calm
            '&:hover': { backgroundColor: '#047857' }
          }}
        >
          {currentUser?.role === 'client' ? 'Adaugare indisponibila' : 'Adauga animal'}
        </Button>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Animals Grid - Carduri patrate */}
      {!isLoading && !error && (
        <>
          {currentUser?.role === 'client' && selectedAnimal ? (
            /* Pentru clienti - afiseaza detaliile animalului cu tabs */
            <Box>
              <Button 
                startIcon={<ArrowBack />}
                onClick={() => setSelectedAnimal(null)}
                sx={{ mb: 3 }}
              >
                Inapoi la lista de animale
              </Button>
              
              {/* Header animal selectat */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={3}>
                    <Box 
                      sx={{ 
                        position: 'relative',
                        '&:hover .avatar-overlay': {
                          opacity: 1
                        }
                      }}
                      onClick={(e) => handlePhotoClick(e, selectedAnimal.id)}
                    >
                      <Avatar
                        src={selectedAnimal.profileImage}
                        sx={{ width: 80, height: 80, bgcolor: '#059669', cursor: 'pointer' }}
                      >
                        {selectedAnimal.name.charAt(0)}
                      </Avatar>
                      <Box
                        className="avatar-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: uploadingPhotoFor === selectedAnimal.id ? 1 : 0,
                          transition: 'opacity 0.3s ease',
                          cursor: 'pointer'
                        }}
                      >
                        {uploadingPhotoFor === selectedAnimal.id ? (
                          <CircularProgress size={32} sx={{ color: 'white' }} />
                        ) : (
                          <PhotoCamera sx={{ color: 'white', fontSize: 32 }} />
                        )}
                      </Box>
                    </Box>
                    <Box flex={1}>
                      {editingName ? (
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                          <TextField
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              '& .MuiInputBase-root': { 
                                fontSize: '2rem',
                                fontWeight: 'bold'
                              }
                            }}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSaveName}
                            disabled={savingName}
                          >
                            {savingName ? 'Se salveaza...' : 'Salveaza'}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setEditingName(false);
                              setNewName(selectedAnimal.name);
                            }}
                          >
                            Anuleaza
                          </Button>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                          <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            {selectedAnimal.name}
                          </Typography>
                          {currentUser?.role !== 'client' && (
                            <IconButton
                              size="small"
                              onClick={() => setEditingName(true)}
                              sx={{ ml: 1 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                      
                      {/* Banner pentru pacienții în amintire sau înstrăinați */}
                      {(selectedAnimal?.status === 'deceased' || selectedAnimal?.status === 'transferred') && (
                        <Alert 
                          sx={{ 
                            mb: 2,
                            backgroundColor: '#F0F4F8',
                            color: '#1F2937',
                            borderColor: '#D1D9E0',
                            '& .MuiAlert-icon': {
                              color: '#1F2937'
                            }
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            {selectedAnimal?.status === 'deceased' ? '🕊️ Pacient "In amintire"' : '📤 Pacient înstrăinat'}
                          </Typography>
                          <Typography variant="body2">
                            {selectedAnimal?.status === 'deceased' ? (
                              <>
                                {selectedAnimal?.deceasedAt && `A trecut în amintire la ${new Date(selectedAnimal.deceasedAt).toLocaleDateString('ro-RO')}.`}
                                {selectedAnimal?.deceasedReason && ` Detalii: ${selectedAnimal.deceasedReason.length > 50 ? selectedAnimal.deceasedReason.substring(0, 50) + '...' : selectedAnimal.deceasedReason}`}
                              </>
                            ) : (
                              <>
                                {selectedAnimal?.deceasedAt && `Înstrăinat la ${new Date(selectedAnimal.deceasedAt).toLocaleDateString('ro-RO')}.`}
                                {selectedAnimal?.deceasedReason && ` Detalii: ${selectedAnimal.deceasedReason.length > 50 ? selectedAnimal.deceasedReason.substring(0, 50) + '...' : selectedAnimal.deceasedReason}`}
                              </>
                            )}
                          </Typography>
                          <Button
                            size="small"
                            sx={{ mt: 1, textDecoration: 'underline' }}
                            onClick={() => setActiveTab(7)}
                          >
                            Vezi detalii
                          </Button>
                        </Alert>
                      )}
                      
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedAnimal.breed} • {selectedAnimal.gender === 'male' ? 'Mascul' : 'Femela'}
                        {(selectedAnimal?.status === 'deceased' || selectedAnimal?.status === 'transferred') && (
                          <Chip 
                            label={selectedAnimal?.status === 'deceased' ? "🕊️ In amintire" : "📤 Înstrăinat"} 
                            size="small" 
                            sx={{ ml: 1, backgroundColor: '#F0F4F8', color: '#1F2937' }}
                            title={selectedAnimal?.status === 'deceased' 
                              ? "Pacientul este în amintire. Dosarul rămâne disponibil pentru consultare."
                              : "Pacientul a fost înstrăinat către un alt proprietar."
                            }
                          />
                        )}
                      </Typography>
                      <Box display="flex" gap={2}>
                        {selectedAnimal.weight && (
                          <Chip label={`${selectedAnimal.weight} kg`} size="small" />
                        )}
                        {selectedAnimal.microchipId && (
                          <Chip label={`Microchip: ${selectedAnimal.microchipId}`} size="small" />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Tabs pentru detaliile animalului */}
              <Card>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab 
                    icon={<AssessmentIcon />} 
                    label="Dosar medical" 
                    iconPosition="start"
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab 
                    icon={<VaccinesIcon />} 
                    label="Vaccinari" 
                    iconPosition="start"
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab 
                    icon={<TreatmentIcon />} 
                    label="Tratament curent" 
                    iconPosition="start"
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab 
                    icon={<DocumentsIcon />} 
                    label="Documente" 
                    iconPosition="start"
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab 
                    label="Alergii" 
                    iconPosition="start"
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab 
                    icon={<ReproIcon />}
                    label="Reproducere"
                    iconPosition="start"
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab 
                    label="Sterilizare" 
                    iconPosition="start"
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab 
                    label="In amintirea pacientului" 
                    iconPosition="start"
                    sx={{ 
                      textTransform: 'none',
                      color: 'text.secondary', 
                      '&.Mui-selected': { 
                        color: 'text.primary',
                        backgroundColor: '#F0F4F8'
                      }
                    }}
                  />
                </Tabs>

                <CardContent>
                  <TabBox sx={{ minHeight: 300 }}>
                    {activeTab === 0 && (
                      <Box display="flex" flexDirection="column" gap={3}>
                        <Alert severity="info">
                          Proprietarul vede aici istoricul medical owner-safe: consultatii finalizate,
                          documente publicate pentru client si detaliile medicale publicate din vizita.
                          Notitele interne de lucru ale clinicii raman vizibile doar personalului medical.
                        </Alert>

                        {loadingMedicalHistory ? (
                          <Box display="flex" alignItems="center" gap={2} py={2}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="text.secondary">
                              Se incarca istoricul medical al animalului...
                            </Typography>
                          </Box>
                        ) : medicalHistoryError ? (
                          <Alert severity="error">{medicalHistoryError}</Alert>
                        ) : (
                          <>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <Chip label={`${completedMedicalHistory.length} consultatii finalizate`} size="small" />
                              <Chip label={`${medicalDocuments.length} documente vizibile`} size="small" />
                              {selectedAnimal.allergies && selectedAnimal.allergies.length > 0 && (
                                <Chip label={`${selectedAnimal.allergies.length} alergii`} size="small" color="warning" />
                              )}
                              {selectedAnimal.chronicConditions && selectedAnimal.chronicConditions.length > 0 && (
                                <Chip label={`${selectedAnimal.chronicConditions.length} afectiuni cronice`} size="small" color="info" />
                              )}
                            </Box>

                            <Box>
                              <Typography variant="h6" gutterBottom>
                                Istoric consultatii
                              </Typography>
                              {completedMedicalHistory.length === 0 ? (
                                <Alert severity="info">
                                  Nu exista inca vizite finalizate disponibile pentru acest animal.
                                </Alert>
                              ) : (
                                <List sx={{ py: 0 }}>
                                  {completedMedicalHistory.slice(0, 5).map((appointment) => (
                                    <ListItem
                                      key={appointment.id}
                                      sx={{
                                        px: 0,
                                        py: 1.5,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        flexWrap: 'wrap'
                                      }}
                                    >
                                      <ListItemText
                                        primary={appointment.reason || 'Consultatie finalizata'}
                                        secondary={
                                          <>
                                            <Typography component="span" variant="body2" display="block" color="text.secondary">
                                              {new Date(appointment.start_ts).toLocaleDateString('ro-RO')} • {appointment.clinic?.name || 'Clinica'}
                                            </Typography>
                                            <Typography component="span" variant="body2" display="block" color="text.secondary">
                                              Medic: {appointment.doctor?.name || 'Nespecificat'}
                                            </Typography>
                                            <Typography component="span" variant="body2" display="block" color="text.secondary">
                                              Deschide detaliul vizitei pentru nota SOAP publicata, daca a fost publicata de clinica.
                                            </Typography>
                                          </>
                                        }
                                      />
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => navigate(`/appointments/${appointment.id}`)}
                                      >
                                        Vezi detalii
                                      </Button>
                                    </ListItem>
                                  ))}
                                </List>
                              )}
                            </Box>

                            <Box>
                              <Typography variant="h6" gutterBottom>
                                Sumar medical vizibil clientului
                              </Typography>
                              {selectedAnimal.allergies && selectedAnimal.allergies.length > 0 ? (
                                <Box mb={2}>
                                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Alergii
                                  </Typography>
                                  <Box display="flex" gap={1} flexWrap="wrap">
                                    {selectedAnimal.allergies.map((allergy, index) => (
                                      <Chip key={`${allergy}-${index}`} label={allergy} size="small" color="warning" variant="outlined" />
                                    ))}
                                  </Box>
                                </Box>
                              ) : null}

                              {selectedAnimal.chronicConditions && selectedAnimal.chronicConditions.length > 0 ? (
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Afectiuni cronice
                                  </Typography>
                                  <Box display="flex" gap={1} flexWrap="wrap">
                                    {selectedAnimal.chronicConditions.map((condition, index) => (
                                      <Chip key={`${condition}-${index}`} label={condition} size="small" color="info" variant="outlined" />
                                    ))}
                                  </Box>
                                </Box>
                              ) : (
                                <Alert severity="info">
                                  Nu exista alergii sau afectiuni cronice publicate pentru acest animal.
                                </Alert>
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    )}
                    {activeTab === 1 && (
                      <Typography variant="body1" color="text.secondary">
                        Nu exista vaccinari inregistrate inca.
                      </Typography>
                    )}
                    {activeTab === 2 && (
                      <Typography variant="body1" color="text.secondary">
                        Nu exista tratamente active in momentul de fata.
                      </Typography>
                    )}
                    {activeTab === 3 && (
                      <Box display="flex" flexDirection="column" gap={2}>
                        {loadingMedicalHistory ? (
                          <Box display="flex" alignItems="center" gap={2} py={2}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="text.secondary">
                              Se incarca documentele medicale...
                            </Typography>
                          </Box>
                        ) : medicalHistoryError ? (
                          <Alert severity="error">{medicalHistoryError}</Alert>
                        ) : medicalDocuments.length === 0 ? (
                          <Alert severity="info">
                            Nu exista documente medicale vizibile pentru acest animal.
                          </Alert>
                        ) : (
                          <List sx={{ py: 0 }}>
                            {medicalDocuments.map((document) => (
                              <ListItem
                                key={document.id}
                                sx={{
                                  px: 0,
                                  py: 1.5,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  gap: 2,
                                  flexWrap: 'wrap'
                                }}
                              >
                                <ListItemText
                                  primary={document.original_filename || document.filename || 'Document medical'}
                                  secondary={
                                    <>
                                      <Typography component="span" variant="body2" display="block" color="text.secondary">
                                        Categorie: {document.category || 'other'}
                                      </Typography>
                                      <Typography component="span" variant="body2" display="block" color="text.secondary">
                                        Incarcat la {document.uploaded_at ? new Date(document.uploaded_at).toLocaleDateString('ro-RO') : 'data necunoscuta'}
                                      </Typography>
                                    </>
                                  }
                                />
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleMedicalDocumentDownload(
                                    document.id,
                                    document.original_filename || document.filename || 'document'
                                  )}
                                >
                                  Descarca
                                </Button>
                              </ListItem>
                            ))}
                          </List>
                        )}

                        <Box>
                          <Button variant="text" onClick={() => navigate('/documents')}>
                            Vezi toate documentele
                          </Button>
                        </Box>
                      </Box>
                    )}
                    {activeTab === 4 && (
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Alergii & Intolerante
                        </Typography>
                        {selectedAnimal.allergies && selectedAnimal.allergies.length > 0 ? (
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {selectedAnimal.allergies.map((allergy, index) => (
                              <Chip key={`${allergy}-${index}`} label={allergy} size="small" color="warning" variant="outlined" />
                            ))}
                          </Box>
                        ) : (
                          <Alert severity="info">
                            Nu exista alergii sau intolerante inregistrate pentru acest animal.
                          </Alert>
                        )}
                      </Box>
                    )}
                    {activeTab === 5 && (
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Alert severity="info">
                          Modulul de reproducere nu are flow owner dedicat complet in aceasta versiune.
                        </Alert>
                        {currentUser?.role === 'client' ? null : (
                        <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setOpenLitterDialog(true)}
                            sx={{ backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
                          >
                            Adaugare fatare
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<PedigreeIcon />}
                            onClick={() => setOpenPedigreeModal(true)}
                          >
                            Pedigree
                          </Button>
                        </Box>
                        )}

                        {currentUser?.role === 'client' ? (
                          <Typography variant="body1" color="text.secondary">
                            Pentru detalii despre reproducere contacteaza clinica sau foloseste un flow administrativ dedicat.
                          </Typography>
                        ) : isLoadingLitters ? (
                          <Typography variant="body1" color="text.secondary">Se incarca...</Typography>
                        ) : litters.length === 0 ? (
                          <Box
                            sx={{
                              p: 3, border: '1px dashed', borderColor: 'divider',
                              borderRadius: 2, textAlign: 'center'
                            }}
                          >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              Nu există fătări înregistrate.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Adaugă prima fătare pentru a lega pui prin microcip.
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<Add />}
                              onClick={() => setOpenLitterDialog(true)}
                              sx={{ backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
                            >
                              Adaugare fatare
                            </Button>
                          </Box>
                        ) : (
                          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                            {litters.map((litter) => {
                              const whelp =
                                (litter as any).whelping_date ?? (litter as any).whelpingDate ?? '—';
                              const pupsDecl =
                                (litter as any).pups_declared ?? (litter as any).pupsDeclared ?? 0;
                              const complete =
                                (litter as any).is_complete ?? (litter as any).completed ?? false;
                              const fatherName =
                                litter.father?.name ??
                                ((litter as any).father_id ? 'Înregistrat în sistem' : 'Necunoscut');
                              return (
                                <Card key={litter.id}>
                                  <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Fătare din {whelp}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={complete ? 'Completă' : 'Incompletă'}
                                        color={complete ? 'success' : 'warning'}
                                      />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                      Tată: {fatherName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      Număr pui declarați: {pupsDecl}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                      <Button size="small" onClick={() => openLitterEdit(litter.id)}>
                                        Editează fătare
                                      </Button>
                                      <Button size="small" onClick={() => openLitterPups(litter.id)}>
                                        Gestionează pui
                                      </Button>
                                      <Button size="small" color="error" onClick={() => deleteLitterRow(litter.id)}>
                                        Șterge fătare
                                      </Button>
                                    </Box>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    )}
                    {activeTab === 6 && (
                      <Box>
                        {currentUser?.role === 'client' ? (
                          <Alert severity="info">
                            Statusul de sterilizare este informativ in flow-ul owner actual si se actualizeaza din clinica.
                          </Alert>
                        ) : (
                        <>
                        <Typography variant="h6" gutterBottom>
                          Informatii Sterilizare
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            Status Sterilizare
                          </Typography>
                          <Box display="flex" gap={2} mb={2}>
                            <Button
                              variant={sterilizationStatus === 'DA' ? 'contained' : 'outlined'}
                              color="success"
                              onClick={() => setSterilizationStatus('DA')}
                            >
                              DA
                            </Button>
                            <Button
                              variant={sterilizationStatus === 'NU' ? 'contained' : 'outlined'}
                              color="secondary"
                              onClick={() => setSterilizationStatus('NU')}
                            >
                              NU
                            </Button>
                          </Box>
                        </Box>

                        {sterilizationStatus === 'DA' && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Data Sterilizarii
                            </Typography>
                            <TextField
                              type="date"
                              value={sterilizationDate}
                              onChange={(e) => setSterilizationDate(e.target.value)}
                              variant="outlined"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          </Box>
                        )}

                        <Box display="flex" gap={2}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveSterilization}
                            disabled={savingSterilization || !sterilizationStatus}
                          >
                            {savingSterilization ? 'Se salveaza...' : 'Salveaza'}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setSterilizationStatus(selectedAnimal?.isNeutered ? 'DA' : 'NU');
                              setSterilizationDate(selectedAnimal?.neuteredDate || '');
                            }}
                          >
                            Reseteaza
                          </Button>
                        </Box>
                        </>
                        )}
                      </Box>
                    )}
                    {activeTab === 7 && (
                      <Box>
                        {currentUser?.role === 'client' ? (
                          <Alert severity="warning">
                            Actiunile de tip "in amintire" sau "instrainare" nu sunt disponibile in flow-ul owner actual.
                          </Alert>
                        ) : (
                        <>
                        <Alert 
                          sx={{ 
                            mb: 3,
                            backgroundColor: '#F0F4F8',
                            color: '#1F2937',
                            borderColor: '#D1D9E0',
                            '& .MuiAlert-icon': {
                              color: '#1F2937'
                            }
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            🕊️ Atenție: Acțiunile din această secțiune sunt permanente.
                          </Typography>
                          <Typography variant="body2">
                            Odată confirmată trecerea pacientului în amintire, modificările nu mai pot fi anulate.
                          </Typography>
                        </Alert>

                        {selectedAnimal?.status === 'active' || !selectedAnimal?.status ? (
                          <Box>
                            <Typography variant="h6" gutterBottom sx={{ color: '#1F2937' }}>
                              Marcare ca "In amintire"
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                              <TextField
                                type="date"
                                label="Data trecerii"
                                value={irrevocableEventDate}
                                onChange={(e) => setIrrevocableEventDate(e.target.value)}
                                variant="outlined"
                                fullWidth
                                sx={{ mb: 2 }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField
                                label="Detalii suplimentare (optional)"
                                value={irrevocableEventDetails}
                                onChange={(e) => setIrrevocableEventDetails(e.target.value)}
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Ex: Circumstanțe, locul, alte detalii relevante..."
                              />
                            </Box>
                            <Button
                              variant="contained"
                              sx={{ 
                                mb: 3,
                                backgroundColor: '#1F2937',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#374151'
                                }
                              }}
                              onClick={handleMarkAsDeceased}
                              disabled={savingIrrevocable || !irrevocableEventDate}
                            >
                              {savingIrrevocable ? 'Se proceseaza...' : 'Confirma trecerea în amintire'}
                            </Button>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" gutterBottom sx={{ color: '#1F2937' }}>
                              Înstrăinare pacient
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                              <TextField
                                type="email"
                                label="Email noul proprietar *"
                                value={transferEmail}
                                onChange={(e) => {
                                  setTransferEmail(e.target.value);
                                  handleEmailVerification(e.target.value);
                                }}
                                variant="outlined"
                                fullWidth
                                sx={{ mb: 2 }}
                                error={emailVerificationStatus === 'not_exists'}
                                helperText={
                                  emailVerificationStatus === 'checking' ? 'Se verifică email-ul...' :
                                  emailVerificationStatus === 'exists' ? '✅ Email-ul există în sistem' :
                                  emailVerificationStatus === 'not_exists' ? '⚠️ Email-ul nu există. Noul proprietar va primi instrucțiuni pentru crearea contului.' :
                                  ''
                                }
                              />
                              <TextField
                                type="date"
                                label="Data înstrăinării *"
                                value={transferDate}
                                onChange={(e) => setTransferDate(e.target.value)}
                                variant="outlined"
                                fullWidth
                                sx={{ mb: 2 }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField
                                label="Detalii despre înstrăinare (optional)"
                                value={transferDetails}
                                onChange={(e) => setTransferDetails(e.target.value)}
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Ex: Motivul înstrăinării, condiții speciale, etc..."
                              />
                            </Box>
                            <Button
                              variant="contained"
                              sx={{ 
                                backgroundColor: '#1F2937',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#374151'
                                }
                              }}
                              onClick={handleTransferAnimal}
                              disabled={savingTransfer || !transferEmail || !transferDate}
                            >
                              {savingTransfer ? 'Se procesează...' : 'Confirma înstrăinarea'}
                            </Button>

                          </Box>
                        ) : (
                          <Alert 
                            sx={{ 
                              backgroundColor: '#F0F4F8',
                              color: '#1F2937',
                              borderColor: '#D1D9E0'
                            }}
                          >
                            <Typography variant="h6" gutterBottom>
                              {selectedAnimal?.status === 'deceased' ? '🕊️ Pacient "In amintire"' : '📤 Pacient înstrăinat'}
                            </Typography>
                            <Typography variant="body2">
                              {selectedAnimal?.status === 'deceased' ? (
                                <>
                                  {selectedAnimal?.deceasedAt && `A trecut în amintire la ${new Date(selectedAnimal.deceasedAt).toLocaleDateString('ro-RO')}.`}
                                  {selectedAnimal?.deceasedReason && ` Detalii: ${selectedAnimal.deceasedReason}`}
                                </>
                              ) : (
                                <>
                                  {selectedAnimal?.deceasedAt && `Înstrăinat la ${new Date(selectedAnimal.deceasedAt).toLocaleDateString('ro-RO')}.`}
                                  {selectedAnimal?.deceasedReason && ` Detalii: ${selectedAnimal.deceasedReason}`}
                                </>
                              )}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {selectedAnimal?.status === 'deceased' 
                                ? 'Dosarul medical rămâne disponibil doar pentru consultare.'
                                : 'Pacientul a fost înstrăinat către un alt proprietar.'
                              }
                            </Typography>
                          </Alert>
                        )}
                        </>
                        )}
                      </Box>
                    )}
                  </TabBox>
                </CardContent>
              </Card>
            </Box>
          ) : (
            /* Lista de animale */
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 3 
            }}>
              {filteredAnimals.map((animal) => (
                <Card key={animal.id} sx={{ 
                  borderRadius: 3, 
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: currentUser?.role === 'client' ? 'pointer' : 'default',
                  '&:hover': { 
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    transform: 'translateY(-4px)'
                  }
                }}
                onClick={() => {
                  if (currentUser?.role === 'client') {
                    setSelectedAnimal(animal);
                    setActiveTab(0);
                  }
                }}>
                  {/* Poza animalului cu overlay pentru schimbare */}
                  <Box 
                    sx={{ 
                      position: 'relative',
                      '&:hover .photo-overlay': {
                        opacity: 1
                      }
                    }}
                    onClick={(e) => {
                      if (currentUser?.role !== 'client') {
                        handlePhotoClick(e, animal.id);
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={animal.profileImage || `https://via.placeholder.com/300x200/059669/ffffff?text=${animal.name.charAt(0)}`}
                      alt={animal.name}
                      sx={{
                        objectFit: 'cover',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    />
                    {currentUser?.role !== 'client' && (
                      <Box
                        className="photo-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: uploadingPhotoFor === animal.id ? 1 : 0,
                          transition: 'opacity 0.3s ease',
                          cursor: 'pointer',
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        {uploadingPhotoFor === animal.id ? (
                          <CircularProgress size={48} sx={{ color: 'white' }} />
                        ) : (
                          <Box sx={{ textAlign: 'center', color: 'white' }}>
                            <PhotoCamera sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="body2">
                              Schimbă poza
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                  
                  <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                        {animal.name}
                        {(animal?.status === 'deceased' || animal?.status === 'transferred') && (
                          <Chip 
                            label={animal?.status === 'deceased' ? "🕊️ In amintire" : "📤 Înstrăinat"} 
                            size="small" 
                            sx={{ ml: 1, backgroundColor: '#F0F4F8', color: '#1F2937' }}
                            title={animal?.status === 'deceased' 
                              ? "Pacientul este în amintire. Dosarul rămâne disponibil pentru consultare."
                              : "Pacientul a fost înstrăinat către un alt proprietar."
                            }
                          />
                        )}
                      </Typography>
                      <Chip 
                        label={animal.species === 'dog' ? 'Caine' : 
                               animal.species === 'cat' ? 'Pisica' : 
                               animal.species === 'bird' ? 'Pasare' : 
                               animal.species === 'rabbit' ? 'Iepure' : 
                               animal.species === 'horse' ? 'Cal' : 'Altul'} 
                        size="small"
                        sx={{
                          backgroundColor: animal.species === 'dog' ? '#059669' : 
                                          animal.species === 'cat' ? '#7c3aed' : 
                                          animal.species === 'bird' ? '#0891b2' : 
                                          animal.species === 'rabbit' ? '#dc2626' : 
                                          animal.species === 'horse' ? '#f59e0b' : '#6b7280',
                          color: 'white',
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {animal.breed} • {animal.gender === 'male' ? 'Mascul' : 'Femela'}
                    </Typography>
                    
                    {animal.weight && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Greutate:</strong> {animal.weight} kg
                      </Typography>
                    )}
                    
                    {animal.microchipId && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Microchip:</strong> {animal.microchipId}
                      </Typography>
                    )}
                    
                    {currentUser?.role !== 'client' && (
                      <Box sx={{ mt: 'auto', display: 'flex', gap: 1, pt: 2 }}>
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAnimal(animal);
                          }}
                          size="small"
                          sx={{ 
                            color: '#0891b2',
                            '&:hover': { backgroundColor: '#f0f9ff' }
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAnimal(animal.id);
                          }}
                          size="small"
                          sx={{ 
                            color: '#dc2626',
                            '&:hover': { backgroundColor: '#fef2f2' }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Animal Form Dialog */}
      <AnimalFormDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleSaveAnimal}
        animal={editingAnimal}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        isLoading={isLoading}
      />

      {/* Floating Action Button for mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={handleAddAnimal}
          disabled={currentUser?.role === 'client'}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            backgroundColor: '#059669',
            '&:hover': { backgroundColor: '#047857' }
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Input ascuns pentru upload poză */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        aria-label="Incarca poza animal"
        style={{ display: 'none' }}
        onChange={handlePhotoUpload}
      />

      {/* Snackbar pentru notificari */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog Adaugare Fatare */}
      {selectedAnimal && (
        <LitterDialog
          open={openLitterDialog}
          onClose={() => setOpenLitterDialog(false)}
          motherId={selectedAnimal.gender === 'female' ? selectedAnimal.id : undefined}
          fatherId={selectedAnimal.gender === 'male' ? selectedAnimal.id : undefined}
          onSuccess={() => {
            setOpenLitterDialog(false);
            setSnackbar({
              open: true,
              message: 'Fătare adăugată cu succes!',
              severity: 'success'
            });
            if (activeTab === 5) {
              reloadLitters();
            }
          }}
        />
      )}

      <Dialog open={litterEditorOpen} onClose={() => !litterSaving && setLitterEditorOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editează fătare</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Data fătării"
            type="date"
            value={litterEditDate}
            onChange={(e) => setLitterEditDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            disabled={litterSaving}
          />
          {currentUser?.role !== 'client' ? (
            <TextField
              label="Note"
              value={litterEditNotes}
              onChange={(e) => setLitterEditNotes(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              disabled={litterSaving}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={litterSaving} onClick={() => setLitterEditorOpen(false)}>
            Anulează
          </Button>
          <Button variant="contained" disabled={litterSaving} onClick={saveLitterEdit}>
            {litterSaving ? 'Se salvează…' : 'Salvează'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={litterPupsOpen} onClose={() => setLitterPupsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pui înregistrați</DialogTitle>
        <DialogContent>
          {!activeLitter || !activeLitter.pups?.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Nu există pui legați încă. Poți adăuga legături din fluxul de reproducere / microcip.
            </Typography>
          ) : (
            <List dense>
              {activeLitter.pups.map((pup: any) => (
                <ListItem key={pup.id}>
                  <ListItemText
                    primary={pup.name || 'Fără nume'}
                    secondary={[pup.breed, pup.microchipId || pup.microchip_id].filter(Boolean).join(' · ')}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLitterPupsOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Pedigree */}
      {selectedAnimal && (
        <PedigreeModal
          open={openPedigreeModal}
          onClose={() => setOpenPedigreeModal(false)}
          animalId={selectedAnimal.id}
          animalName={selectedAnimal.name}
        />
      )}
    </Container>
  );
};

const Animals: React.FC = () => {
  const { user } = useAuth();
  if (user?.role === 'client') {
    return <ClientAnimalsModule />;
  }
  return <ClinicAnimalsManagement />;
};

export default Animals;
